<template>
  <div class="subscriptions">
    <div class="container view" v-if="pricing && subscriptions">
      <Card class="subscriptions__card">
        <template #header>
          <h1 class="subscriptions__title">{{ $t('subscriptionOverview') }}</h1>
          <span class="subscriptions__subtitle">{{ $t('pricesInExludingVat') }}</span>
          <Alert class="subscriptions__alert" iconPlacement="left" :icon="['fal', 'info-circle']" theme="primary">
            {{ $t('subscriptionOverviewAlert', { x: '' }) }}
            <a :href="`mailto:${email}`" target="_blank">{{ email }}</a>
          </Alert>
        </template>

        <template #default>
          <h2>{{ $t('basicPackage') }}</h2>
          <Card theme="border" class="subscriptions__row-card">
            <template #default>
              <Row v-for="(standardItem, index) in standard" :row="standardItem" :key="index">
                <!-- <template #trigger="{ trigger }">{{ $t('readMore') }}</template> -->
              </Row>
            </template>
          </Card>

          <template v-if="projects.length">
            <h2>{{ $tc('project', 2) }}</h2>
            <Card theme="border" class="subscriptions__row-card">
              <template #default>
                <Row v-for="(projectItem, index) in projects" :row="projectItem" :key="index">
                  <template #trigger="{ open }">
                    <FontAwesomeIcon :icon="['fas', open() ? 'chevron-up' : 'chevron-down']" />
                  </template>
                  <template #content>
                    <DataTable
                      :row-wrapper="
                        (item, row) => {
                          return item.addon === 'project'
                            ? { component: 'div' }
                            : {
                                component: 'div',
                                listeners: { click: openModal.bind(null, item, projectItem.id) },
                                props: {
                                  style: 'cursor: pointer',
                                },
                              }
                        }
                      "
                      :items="projectItem.defaultAddons"
                      :columns="[
                        { id: 'label', label: $t('standardModules') },
                        { id: 'status', label: $t('status') },
                        { id: 'activate', label: $t('activationDate') },
                        { id: 'price', label: $t('price'), align: 'end' },
                      ]"
                    >
                      <template #secondary="{ item, rowIndex }">
                        <!-- <ButtonComponent
                        v-if="item.status !== 'active'"
                        :icon="['fal', 'external-link']"
                        :label="$t('readMore')"
                      /> -->

                        <Modal
                          :ref="`${item.addon}:${projectItem.id}`"
                          key="cancel"
                          v-if="item.status === 'active' && item.addon !== 'project'"
                          :title="$t(`addon:${item.addon}:cancel`)"
                          :description="$t(`addon:${item.addon}:cancel:description`)"
                          :triggerLabel="$t('terminateAgreement')"
                          :items="[
                            {
                              value: 'completedProject',
                              label: $t('completedProject'),
                            },
                            {
                              value: 'economy',
                              label: $t('economy'),
                            },
                            {
                              value: 'systemFailure',
                              label: $t('systemFailure'),
                              textField: true,
                              textFieldLabel: $t('explainImprovements'),
                              required: true,
                            },
                            {
                              value: 'other',
                              label: 'Annet',
                              textField: true,
                              textFieldLabel: $t('canYouExplain'),
                            },
                          ]"
                          @input="
                            reason =>
                              addonHandler({
                                addon: item.addon,
                                reason,
                                onModel: 'Project',
                                id: projectItem.id,
                                state: false,
                              })
                          "
                          :promise="promise"
                        />

                        <Modal
                          :ref="`${item.addon}:${projectItem.id}`"
                          key="activate"
                          align="center"
                          :thumbnail="`/images/subscription/activate/${item.addon}.png`"
                          :title="$t(`addon:${item.addon}:activate`)"
                          :description="$t(`addon:${item.addon}:activate:description`)"
                          v-if="item.status !== 'active'"
                          :triggerLabel="$t('activate')"
                          @input="
                            () =>
                              addonHandler({ addon: item.addon, id: projectItem.id, onModel: 'Project', state: true })
                          "
                          :promise="promise"
                        />

                        <ButtonComponent
                          v-if="$store.getters['isSuperadmin'] && ['active', 'cancelled'].includes(item.status)"
                          tag="router-link"
                          :label="$t('changeX', { x: '' })"
                          :to="{
                            name: 'subscription-edit',
                            params: { id: item.id },
                            query: { redirect: redirectUrl },
                          }"
                        />
                      </template>
                    </DataTable>

                    <DataTable
                      :items="projectItem.extraAddons"
                      :columns="[
                        { id: 'label', label: $t('additionalProducts') },
                        { id: 'status', label: $t('status') },
                        { id: 'activate', label: $t('activationDate') },
                        { id: 'price', label: $t('price'), align: 'end' },
                      ]"
                    >
                      <template #secondary="{ item, rowIndex }">
                        <ButtonComponent
                          v-if="item.addon === 'project:integration-finn'"
                          :label="item.status !== 'active' ? $t('activate') : $t('terminateAgreement')"
                          :icon="['fal', 'long-arrow-right']"
                          tag="router-link"
                          :to="{
                            name: 'project-edit',
                            params: {
                              id: projectItem.id,
                              step: 'integrations',
                            },
                            query: { category: 'tracking-marketing' },
                          }"
                        />
                      </template>
                    </DataTable>
                  </template>
                </Row>
              </template>
            </Card>
          </template>

          <template v-if="pages.length">
            <h2>{{ $tc('page', 2) }}</h2>
            <Card theme="border" class="subscriptions__row-card">
              <template #default>
                <Row v-for="(pageItem, index) in pages" :row="pageItem" :key="index"> </Row>
              </template>
            </Card>
          </template>
        </template>

        <template #footer>
          <div class="subscriptions-total-price__label-wrapper">
            <span class="subscriptions-total-price__label">{{ $t('collectedMonthPrice') }}</span>
            <span class="subscriptions-total-price__sublabel">{{ $t('moduleInvoiceDatePrice') }}</span>
          </div>
          <div class="subscriptions-total-price__value-wrapper">
            <span class="subscriptions-total-price__value">kr {{ totalPrice.toLocaleString('nb-no') }}</span>
            <span class="subscriptions-total-price__suffix"> /mnd</span>
          </div>
        </template>
      </Card>
    </div>
  </div>
</template>

<script>
import Card from '@/components/Card'
import InputDate from '@/components/InputDate'
import { InputComponent } from 'vue-elder-input'
import { AlertComponent as Alert } from 'vue-elder-alert'

import Row from '@/components/Subscription/Row'
import DataTable from '@/components/Subscription/DataTable'
import Modal from '@/components/Subscription/Modal'

import SubscriptionApi from '@/api/Subscription'
import ProjectApi from '@/api/Project'
import PageApi from '@/api/Page'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

function removeDuplicates(arr) {
  return [...new Set(arr)]
}

function getPrice(prices, types = ['monthly']) {
  return prices.reduce((result, price) => {
    if (types.includes(price.type)) result += price.value
    return result
  }, 0)
}

export default {
  data() {
    return {
      pricing: null,
      subscriptions: null,
      promise: null,
    }
  },
  computed: {
    email() {
      return 'faktura@kvass.no'
    },

    redirectUrl() {
      return window.location.href
    },

    totalPrice() {
      return [...this.standard, ...this.pages, ...this.projects].reduce((total, item) => (total += item.price), 0)
    },

    standard() {
      return ['app', 'custom-contract', 'api'].map(key => {
        const item = this.subscriptions.find(s => s.item === key) || this.pricing.items.find(i => i.id === key)

        return {
          label: this.$t(`addon:${key}`),
          price: getPrice(item.price),
        }
      })
    },

    pages() {
      return this.subscriptions
        .filter(s => this.$path('reference.onModel', s) === 'Page')
        .map(sub => {
          return {
            id: this.$path('reference.ref._id', sub),
            sublabel: this.$path('reference.ref.publishing.domain', sub),
            label: sub.metadata.source.name,
            price: getPrice(sub.price),
          }
        })
    },

    projects() {
      const pItems = this.pricing.items.find(i => i.id === 'project')

      const addons = [...pItems.addons.map(i => i.id), pItems.id]

      const defaultAddons = ['project', 'presentation', 'bid', 'purchase', 'shop', 'shop-purchase', 'complaint']

      const exludedAddons = ['flatfinder', 'residential-account']

      const extraAddons = addons.filter(add => !defaultAddons.includes(add) && !exludedAddons.includes(add))

      const projectSubscriptions = this.subscriptions.filter(s => this.$path('reference.onModel', s) === 'Project')

      const ids = removeDuplicates(projectSubscriptions.map(s => this.$path('reference.ref._id', s))).filter(Boolean)

      return ids.map(id => {
        const items = projectSubscriptions.filter(s => this.$path('reference.ref._id', s) === id)

        const limit = items[0].price[0].conditions.units.gte

        return {
          id: id,
          label: items[0].metadata.source.name,
          badge: `${limit}+ ${this.$tc('units', 2).toLowerCase()}`,
          price: getPrice(items.map(i => i.price).flat()),
          defaultAddons: this.getTableData(defaultAddons, items, limit),
          extraAddons: this.getTableData(extraAddons, items, limit),
        }
      })
    },
  },
  methods: {
    openModal(item, projectId) {
      this.$refs[`${item.addon}:${projectId}`][0].open()
    },
    getTableData(addons, items, limit) {
      return addons
        .map(addon => {
          const addonItem = items.find(i => [`project:${addon}`, addon].includes(i.item))

          if (addonItem) {
            return {
              id: addonItem._id,
              addon: addonItem.item,
              status: addonItem.end ? 'cancelled' : 'active',
              start: addonItem.start,
              end: addonItem.end,
              priceMonthly: getPrice(addonItem.price),
              priceFixed: getPrice(addonItem.price, ['fixed', 'establishment']),
            }
          }

          const pItem = this.pricing.steps.find(step => step.value === limit).items.find(i => i.id === 'project')

          const defaultItem = addon === pItem.id ? pItem : pItem.addons.find(i => i.id === addon)

          if (!defaultItem) return

          return {
            addon: defaultItem.id === 'project' ? defaultItem.id : `project:${defaultItem.id}`,
            status: 'notActive',
            start: null,
            end: null,
            priceMonthly: getPrice(defaultItem.price),
            priceFixed: getPrice(defaultItem.price, ['fixed', 'establishment']),
          }
        })
        .filter(Boolean)
    },

    setData() {
      return Promise.all([
        fetch(`/api/subscription/active`).then(res => res.json()),
        fetch(`/api/pricing`).then(res => res.json()),
      ]).then(values => {
        const [subscriptions, pricing] = values

        this.subscriptions = subscriptions
        this.pricing = pricing
      })
    },

    addonHandler(options = {}) {
      const { addon, reason, onModel, id, state } = options

      const getHandler = () => {
        switch (onModel) {
          case 'Project':
            return ProjectApi.addon({ project: id, state, addon, reason })
          case 'Page':
            return PageApi.addon({ page: id, state, addon, reason })
          default:
            return SubscriptionApi.addon({ addon, state, reason })
        }
      }

      return (this.promise = getHandler().then(() => this.setData()))
    },
  },
  created() {
    this.promise = this.setData()
  },
  metaInfo() {
    return {
      title: this.$t('subscriptionOverview'),
    }
  },
  components: {
    Card,
    InputDate,
    InputComponent,
    Alert,
    Row,
    DataTable,
    FontAwesomeIcon,
    Modal,
  },
}
</script>

<style lang="scss">
.subscriptions {
  background-color: $background-grey;

  &__card {
    .card__header {
      gap: 0.5rem;
      align-items: start;
      justify-content: left;
      background-color: inherit;
    }

    .card__footer {
      background-color: hsl(var(--border-color-h), var(--border-color-s), calc(var(--border-color-l) + 9%));
      padding-top: var(--card-spacing);
      &-content {
        justify-content: space-between;
      }
    }
  }

  &-total-price {
    &__label {
      &-wrapper {
        display: flex;
        flex-direction: column;
      }
    }
    &__sublabel {
      font-size: 0.8rem;
      opacity: 0.7;
    }
    &__value {
      font-weight: 700;
      font-size: 1.5rem;
      &-wrapper {
        margin-right: calc(100px + var(--card-spacing) + 1rem);
      }
    }
    &__suffix {
      opacity: 0.7;
    }
  }

  &__title {
    width: 100%;
    border-bottom: 1px solid var(--border-color);
    padding-bottom: 0.5rem;
    margin-bottom: 0;
  }

  &__subtitle {
    font-weight: bold;
    opacity: 0.7;
  }

  &__alert {
    svg {
      font-size: 2rem;
    }

    .alert__content {
      border-radius: inherit;
      &:before {
        border-radius: inherit;
      }
    }
  }

  .container {
    max-width: 1050px;
  }

  &__row-card {
    .card__content {
      padding: 0;
      border-radius: inherit;
      overflow: hidden;
    }
  }
}
</style>
